import type { BuilderContent } from "@builder.io/sdk-react";

import type { ProductHitWithSwatches } from "~/lib/app.types";

/**
 * Per-breakpoint column span. "0" hides the tile at that breakpoint; "1".."4"
 * is the number of grid columns the tile occupies (and therefore the number of
 * grid cells it consumes — see the cell model below).
 */
export type TileSpan = "0" | "1" | "2" | "3" | "4";

export const BREAKPOINTS = [
  "mobile",
  "tablet",
  "desktop",
  "largeDesktop",
] as const;
export type Breakpoint = (typeof BREAKPOINTS)[number];

/**
 * A single PLP Inline Content tile, normalized to *effective* per-breakpoint
 * slot + span values (the loader resolves any model-level base/override into
 * these). `content.value` is the resolved `plp-inline-content` section.
 *
 * Placement model — **slot is an absolute, 1-based grid cell**, and a tile with
 * span N occupies N consecutive cells. Products fill every cell not claimed by
 * a tile, in order. Example (desktop, 4 columns): tiles at (slot 1, span 4),
 * (slot 5, span 2), (slot 8, span 1) render as:
 *   row 1: [tile span 4]
 *   row 2: [tile span 2][product][tile span 1]
 *   row 3: [product][product][product][product] …
 *
 * Slots are stable: scheduling another tile in never shifts an existing tile's
 * slot (see {@link interleaveInlineTiles}).
 */
export interface PlpInlineTileItem {
  content?: {
    "@type"?: string;
    id?: string;
    model?: string;
    value?: BuilderContent | null;
  };
  mobileSlot?: number;
  tabletSlot?: number;
  desktopSlot?: number;
  largeDesktopSlot?: number;
  mobileSpan?: TileSpan;
  tabletSpan?: TileSpan;
  desktopSpan?: TileSpan;
  largeDesktopSpan?: TileSpan;
  // Optional author-provided aspect ratio (e.g. "992/1614") for the wrapper.
  aspectRatio?: string;
}

/** Resolved per-breakpoint span set for one rendered DOM node. */
export type TileSpanSet = Record<Breakpoint, TileSpan>;

export type InlineInterleavedEntry =
  | { kind: "product"; product: ProductHitWithSwatches; key: string }
  | {
      kind: "marketing";
      tile: PlpInlineTileItem;
      spans: TileSpanSet;
      key: string;
    };

const SLOT_KEYS: Record<Breakpoint, keyof PlpInlineTileItem> = {
  mobile: "mobileSlot",
  tablet: "tabletSlot",
  desktop: "desktopSlot",
  largeDesktop: "largeDesktopSlot",
};

const SPAN_KEYS: Record<Breakpoint, keyof PlpInlineTileItem> = {
  mobile: "mobileSpan",
  tablet: "tabletSpan",
  desktop: "desktopSpan",
  largeDesktop: "largeDesktopSpan",
};

function parseSpan(v: unknown): number {
  const n =
    typeof v === "number" ? v : typeof v === "string" ? parseInt(v, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Caps each tile's span at the grid's column count per breakpoint. A span wider
 * than the grid adds implicit columns and breaks the layout, and the placement
 * maths counts spans as cells, so clamp before {@link interleaveInlineTiles}.
 * Hidden ("0") and missing spans are left as they are.
 */
export function clampTileSpans(
  tiles: PlpInlineTileItem[],
  columns: Record<Breakpoint, number>,
): PlpInlineTileItem[] {
  return tiles.map(tile => {
    const clamped = { ...tile };
    for (const bp of BREAKPOINTS) {
      const key = SPAN_KEYS[bp] as `${Breakpoint}Span`;
      const span = parseSpan(tile[key]);
      if (span > columns[bp]) clamped[key] = String(columns[bp]) as TileSpan;
    }
    return clamped;
  });
}

function parseSlot(v: unknown): number | null {
  const n =
    typeof v === "number" ? v : typeof v === "string" ? parseInt(v, 10) : NaN;
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(n, hi));
}

function hasResolvedContent(tile: PlpInlineTileItem): boolean {
  const value = tile.content?.value;
  return !!value && Object.keys(value).length > 0;
}

/**
 * Interleave PLP Inline Content tiles into a flat product list, honouring
 * absolute, stable, per-breakpoint cell slots.
 *
 * For each breakpoint independently, visible tiles (span ≥ 1 and a slot) are
 * placed at their starting cell and products fill the remaining cells. The
 * number of products that must precede a tile is
 *   `productsBefore = clamp(slot − 1 − Σ(span of earlier tiles), 0, products.length)`
 * — i.e. the cells before the tile minus the cells those earlier tiles already
 * consumed. Because `slot` is fixed, inserting another tile at a smaller slot
 * raises the earlier-span sum and lowers this tile's `productsBefore`, but its
 * cell is unchanged — that is the stability SiteOps asked for.
 *
 * The result is a single SSR DOM list. A tile is emitted as **one node per
 * distinct `productsBefore` value** across its breakpoints, visible only at the
 * breakpoints sharing that value (each carrying its real span, "0"/hidden
 * elsewhere). Hidden (`display:none`) nodes consume no grid cell, so every
 * breakpoint's flow lands each tile after the same product run regardless of
 * the other breakpoints. The grid keeps `grid-auto-flow: dense` (matching the
 * legacy path) so multi-column tiles pack without row-wrap gaps; for row-fitting
 * layouts each tile renders at its authored cell. Because the migration sets
 * `slot = oldPosition + 1 + Σ(earlier spans)`, the DOM order this produces is
 * identical to the legacy order, so dense renders migrated content unchanged.
 */
export function interleaveInlineTiles(
  products: ProductHitWithSwatches[],
  tiles: PlpInlineTileItem[],
): InlineInterleavedEntry[] {
  const productEntries: InlineInterleavedEntry[] = products.map(
    (product, i) => ({
      kind: "product",
      product,
      key: `product-${product.productId ?? i}`,
    }),
  );

  if (!tiles.length) return productEntries;

  const total = products.length;

  // tileIndex → (breakpoint → productsBefore) for breakpoints where the tile is
  // placed (visible, with a slot, not overlapping an earlier-placed tile).
  const placements = new Map<number, Map<Breakpoint, number>>();

  for (const bp of BREAKPOINTS) {
    const visible: { tileIdx: number; slot: number; span: number }[] = [];
    tiles.forEach((tile, idx) => {
      if (!hasResolvedContent(tile)) return;
      const span = parseSpan(tile[SPAN_KEYS[bp]]);
      if (span <= 0) return;
      const slot = parseSlot(tile[SLOT_KEYS[bp]]);
      if (slot == null) return;
      visible.push({ tileIdx: idx, slot, span });
    });
    // Lowest slot first; stable on original order for equal slots.
    visible.sort((a, b) => a.slot - b.slot || a.tileIdx - b.tileIdx);

    let consumedCells = 0; // cells consumed by earlier placed tiles
    let lastOccupiedCell = 0; // last cell occupied so far (overlap guard)
    for (const v of visible) {
      // Skip tiles whose start cell collides with an already-placed tile.
      if (v.slot <= lastOccupiedCell) continue;
      const productsBefore = clamp(v.slot - 1 - consumedCells, 0, total);
      consumedCells += v.span;
      lastOccupiedCell = v.slot + v.span - 1;

      let byBp = placements.get(v.tileIdx);
      if (!byBp) {
        byBp = new Map();
        placements.set(v.tileIdx, byBp);
      }
      byBp.set(bp, productsBefore);
    }
  }

  type Insertion = {
    productsBefore: number;
    repSlot: number;
    seq: number;
    entry: InlineInterleavedEntry;
  };
  const insertions: Insertion[] = [];

  tiles.forEach((tile, idx) => {
    const byBp = placements.get(idx);
    if (!byBp || byBp.size === 0) return;

    // Group breakpoints by their productsBefore value: one DOM node per group.
    const grouped = new Map<number, Breakpoint[]>();
    for (const [bp, pb] of byBp) {
      const arr = grouped.get(pb) ?? [];
      arr.push(bp);
      grouped.set(pb, arr);
    }

    let nodeSeq = 0;
    for (const [pb, bps] of grouped) {
      const spans: TileSpanSet = {
        mobile: "0",
        tablet: "0",
        desktop: "0",
        largeDesktop: "0",
      };
      let repSlot = Number.POSITIVE_INFINITY;
      for (const bp of bps) {
        spans[bp] = String(parseSpan(tile[SPAN_KEYS[bp]])) as TileSpan;
        const slot = parseSlot(tile[SLOT_KEYS[bp]]);
        if (slot != null) repSlot = Math.min(repSlot, slot);
      }
      insertions.push({
        productsBefore: pb,
        repSlot: Number.isFinite(repSlot) ? repSlot : pb,
        seq: idx,
        entry: {
          kind: "marketing",
          tile,
          spans,
          key: `inline-${tile.content?.id ?? idx}-${nodeSeq++}`,
        },
      });
    }
  });

  // Stable ordering for nodes that share a productsBefore: lowest slot first,
  // then original tile order.
  insertions.sort(
    (a, b) =>
      a.productsBefore - b.productsBefore ||
      a.repSlot - b.repSlot ||
      a.seq - b.seq,
  );

  const out: InlineInterleavedEntry[] = [];
  let next = 0;
  for (let i = 0; i <= total; i++) {
    while (next < insertions.length && insertions[next].productsBefore === i) {
      out.push(insertions[next].entry);
      next++;
    }
    if (i < total) out.push(productEntries[i]);
  }
  return out;
}

const SPAN_VALUES: ReadonlySet<string> = new Set(["0", "1", "2", "3", "4"]);

function coerceSpan(v: unknown): TileSpan | undefined {
  if (v == null) return undefined;
  const s = String(typeof v === "number" ? Math.floor(v) : v).trim();
  return SPAN_VALUES.has(s) ? (s as TileSpan) : undefined;
}

function coerceSlot(v: unknown): number | undefined {
  const n =
    typeof v === "number" ? v : typeof v === "string" ? parseInt(v, 10) : NaN;
  return Number.isFinite(n) ? Math.floor(n) : undefined;
}

// Grouped per-breakpoint shape the `plp-inline-content-setup` model emits: each
// breakpoint is a collapsible object with an author-facing `position` (the
// 1-based grid cell — internally a "slot") and `span`.
type Bp = { position?: unknown; span?: unknown };
interface RawSetupItem {
  content?: PlpInlineTileItem["content"];
  aspectRatio?: unknown;
  mobile?: Bp;
  tablet?: Bp;
  desktop?: Bp;
  largeDesktop?: Bp;
}

/**
 * Normalize a resolved `plp-inline-content-setup` Builder entry into the flat
 * {@link PlpInlineTileItem}[] the interleave algorithm consumes. Reference
 * values are passed through untouched (the loader enriches them).
 */
export function normalizeInlineSetupItems(
  setupEntry: unknown,
): PlpInlineTileItem[] {
  const items =
    (setupEntry as { data?: { injectedContentItems?: unknown } })?.data
      ?.injectedContentItems ??
    (setupEntry as { injectedContentItems?: unknown })?.injectedContentItems;
  if (!Array.isArray(items)) return [];
  return (items as RawSetupItem[]).map(raw => ({
    content: raw?.content,
    aspectRatio:
      typeof raw?.aspectRatio === "string" ? raw.aspectRatio : undefined,
    mobileSlot: coerceSlot(raw?.mobile?.position),
    mobileSpan: coerceSpan(raw?.mobile?.span),
    tabletSlot: coerceSlot(raw?.tablet?.position),
    tabletSpan: coerceSpan(raw?.tablet?.span),
    desktopSlot: coerceSlot(raw?.desktop?.position),
    desktopSpan: coerceSpan(raw?.desktop?.span),
    largeDesktopSlot: coerceSlot(raw?.largeDesktop?.position),
    largeDesktopSpan: coerceSpan(raw?.largeDesktop?.span),
  }));
}
